import { defineStore } from 'pinia';

import {
  getImage,
  IImage,
  ImageClass,
  initializeImages,
  resetImages,
} from '../common';
import Tr from '@/i18n/translation';
import { useStateStore } from '@/stores/state.store';
import { DataService } from '@/services/index.js';
import ImageService from '../../services/ImageService.js';
import { Logger } from '../../middlewares/sentry.js';
import { ISaveDateImagesStructure, ISaveDateState } from './saveDate.state.js';
import { SaveDateCommonClass } from '../../../interface/data/saveDate/common.js';
import { SaveDateLangClass } from '../../../interface/data/saveDate/lang.js';
import { ImageType } from 'interface/data/image/type';
import { SaveDateDataClass } from 'interface/data/saveDate/data';

class SaveDateImagesStructureClass implements ISaveDateImagesStructure {
  [ImageType.SAVEDATEBACKGROUND]: IImage = new ImageClass();
  [ImageType.SAVEDATEFRONT]: IImage = new ImageClass();
}

export const useSaveDateStore = defineStore('saveDate', {
  state: (): ISaveDateState => ({
    clientImages: initializeImages(SaveDateImagesStructureClass),
    clientData: new SaveDateDataClass(),
    isDataReceived: false,
    isLangDataReceived: false,
  }),
  actions: {
    async trySetupData() {
      await this._setSaveDateCommonData();
      const lang = Tr.currentLocale;
      this.tryGetLangDataWithImages(lang);
    },
    async _setSaveDateCommonData() {
      if (!this.isDataReceived) {
        await this._tryGetSaveDateCommonData();
        this.isDataReceived = true;
      } else {
        new Promise(async () => {
          this._tryGetSaveDateCommonData();
        });
      }

      const { setColors } = useStateStore();
      setColors(
        this.clientData.common.mainColor,
        this.clientData.common.mainColor,
        this.clientData.common.backgroundColor,
      );
    },
    async _tryGetSaveDateCommonData() {
      try {
        const data = await DataService.getSaveDateData();
        this.clientData.common = new SaveDateCommonClass(data);
      } catch (err) {
        Logger.error(err);
        this.clientData.common = new SaveDateCommonClass();
      }
    },
    async tryGetLangDataWithImages(lang: string) {
      await this._setGetLangData(lang);
      this._tryGetClientImages(lang);
    },
    async _setGetLangData(lang: string) {
      if (!this.isLangDataReceived) {
        await this.tryGetLangData(lang);
        this.isLangDataReceived = true;
      } else {
        new Promise(async () => {
          this.tryGetLangData(lang);
        });
      }
    },
    async tryGetLangData(lang: string) {
      try {
        const data = await DataService.getSaveDateData(lang);
        this.clientData.lang[lang] = new SaveDateLangClass(data);
      } catch (err) {
        Logger.error(err);
        if (!this.isLangDataReceived) {
          const { getDefaultLang } = useStateStore();

          if (lang !== getDefaultLang) {
            this.tryGetLangData(getDefaultLang);
            return;
          }

          this.clientData.lang[lang] = new SaveDateLangClass();
        }
      }
    },
    async _tryGetClientImages(lang: string) {
      try {
        if (!this.clientImages[lang]) {
          this.clientImages[lang] = new SaveDateImagesStructureClass();
        }

        const data = await ImageService.getImagesInfo();
        getImage(this.clientImages, data, ImageType.SAVEDATEBACKGROUND, lang);
        getImage(this.clientImages, data, ImageType.SAVEDATEFRONT, lang);
      } catch (err) {
        Logger.error(err);
      }
    },
    getLangData(data: string, lang = Tr.currentLocale) {
      if (!this.clientData.lang[lang]) {
        this.clientData.lang[lang] = new SaveDateLangClass();
      }
      return this.clientData.lang[lang][data];
    },
    getLangAllLangData(lang = Tr.currentLocale) {
      if (!this.clientData.lang[lang]) {
        this.clientData.lang[lang] = new SaveDateLangClass();
      }
      return this.clientData.lang[lang];
    },
    resetLangData(lang: string) {
      this.clientData.lang[lang] = new SaveDateLangClass();
    },
    resetData() {
      this.clientData = new SaveDateDataClass();
      resetImages(this.clientImages);
      this.isDataReceived = false;
      this.isLangDataReceived = false;
    },
  },
  getters: {
    getCommonData: (state) => state.clientData.common,
    getImages: (state) => {
      const lang = Tr.currentLocale;
      if (!state.clientImages[lang]) {
        state.clientImages[lang] = new SaveDateImagesStructureClass();
      }

      return state.clientImages[lang];
    },
    getColors: (state) => {
      return {
        mainColor: state.clientData.common.mainColor,
        backgroundColor: state.clientData.common.backgroundColor,
      };
    },
  },
});
